import React, { useCallback, useEffect, useState } from "react";
import {
  ArrowRightIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ClockIcon,
  CreditCardIcon,
  ReceiptRefundIcon,
} from "@heroicons/react/24/outline";
import { format } from "date-fns";
import { useSelector } from "react-redux";
import {
  transactionApi,
  type Invoice,
} from "../../../repositories/transaction";
import { handleError } from "../../../utils/errorHandler";
import { useUser } from "../../../hooks/useUser";
import { selectUserType } from "../../../store/slices/userSlice";
import { userApi } from "../../../repositories/user";
import DataTable, { DataTableColumn, dataTableRowClass, dataTableTdClass } from '../../ui/DataTable';
import { cn } from '../../../DashboardPage/lib/utils';
import SettingsPanel from '../../ui/SettingsPanel';
import Button from "../../ui/Button";

/**
 * Safely format a date value, returning a fallback if the value is missing or invalid.
 * Guards against `RangeError: Invalid time value` thrown by date-fns' `format`.
 */
const safeFormatDate = (
  value: string | number | Date | null | undefined,
  dateFormat: string,
  fallback = "N/A"
): string => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }
  const date = new Date(value);
  if (isNaN(date.getTime())) {
    return fallback;
  }
  return format(date, dateFormat);
};

// One active pitch pack. Credits stay tied to the pack they were bought with
// and are consumed FIFO, so several packs (each with its own expiry) can be
// active at once.
interface PitchPackItem {
  id: number;
  packageName: string;
  pitchLimit: number;
  expiresAt: string;
  purchaseDate: string;
  isExpired: boolean;
}

interface PurchaseData {
  id: number;
  email: string;
  productId: string;
  status: string;
  transactionId: string;
  // Aggregated remaining credits across all active packs.
  pitchLimit: number;
  userId: number;
  packageName: string;
  // Earliest upcoming expiry among active packs.
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
  isExpired?: boolean;
  // Per-pack breakdown (active packs only), oldest first.
  packs?: PitchPackItem[];
}

interface Price {
  id: string;
  object: string;
  active: boolean;
  billing_scheme: string;
  created: number;
  currency: string;
  custom_unit_amount: number | null;
  livemode: boolean;
  plan_name: string;
  lookup_key: string | null;
  metadata: { [key: string]: string };
  nickname: string | null;
  product: string;
  recurring: any | null;
  tax_behavior: string;
  tiers_mode: string | null;
  transform_quantity: any | null;
  type: string;
  unit_amount: number;
  unit_amount_decimal: string;
}

interface StripeProduct {
  id: string;
  name: string;
  description: string;
  active: boolean;
  metadata: {
    payment_link: string;
    pitch_limit: string;
    product_id: string;
  };
  prices: Price[];
}

interface SubscriptionData {
  status: string;
  current_period_start: string;
  current_period_end: string;
  package_id: string;
  stripe_price: string;
  quantity: number;
  plan_name: string;
}

interface ProfileData {
  purchase: PurchaseData | null;
  subscription?: SubscriptionData | null;
}

// Skeleton component for loading state
const SkeletonLoader = () => {
  return (
    <SettingsPanel loading>
      {/* Header skeleton */}
      <div className="flex items-center justify-between mb-6">
        <div className="w-40 h-8 bg-neutral-200 rounded-md animate-pulse"></div>
        <div className="w-16 h-6 bg-neutral-200 rounded-full animate-pulse"></div>
      </div>

      {/* Current Plan Overview skeleton */}
      <div className="p-6 mb-8 bg-neutral-100 rounded-xl">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div className="mb-4 md:mb-0">
            <div className="w-32 h-6 mb-2 bg-neutral-200 rounded-md animate-pulse"></div>
            <div className="w-40 h-4 bg-neutral-200 rounded-md animate-pulse"></div>
          </div>
          <div className="h-5 bg-neutral-200 rounded-md w-36 animate-pulse"></div>
        </div>
      </div>

      {/* Credit Usage and Expiration skeleton */}
      <div className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-2">
        <div className="p-6 bg-white border border-neutral-200 rounded-xl">
          <div className="w-32 h-4 mb-3 bg-neutral-200 rounded-md animate-pulse"></div>
          <div className="w-20 h-8 mb-4 bg-neutral-200 rounded-md animate-pulse"></div>
        </div>

        <div className="p-6 bg-white border border-neutral-200 rounded-xl">
          <div className="w-32 h-4 mb-3 bg-neutral-200 rounded-md animate-pulse"></div>
          <div className="w-24 h-8 mb-4 bg-neutral-200 rounded-md animate-pulse"></div>
        </div>
      </div>

      {/* Button skeleton */}
      <div className="w-full h-12 mb-8 bg-neutral-200 rounded-lg animate-pulse"></div>

      {/* Available Plans skeleton */}
      <div>
        <div className="w-40 mb-6 bg-neutral-200 rounded-md h-7 animate-pulse"></div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((item) => (
            <div
              key={item}
              className="p-6 bg-white border border-neutral-200 rounded-xl"
            >
              <div className="w-24 h-6 mb-3 bg-neutral-200 rounded-md animate-pulse"></div>
              <div className="w-20 h-8 mb-3 bg-neutral-200 rounded-md animate-pulse"></div>
              <div className="w-full h-4 mb-6 bg-neutral-200 rounded-md animate-pulse"></div>
              <div className="w-full h-10 mt-4 bg-neutral-200 rounded-md animate-pulse"></div>
            </div>
          ))}
        </div>
      </div>
    </SettingsPanel>
  );
};

const HISTORY_PAGE_SIZE = 5;

// Collapsible purchase history with "Load more" pagination.
const PurchaseHistory: React.FC = () => {
  const [open, setOpen] = useState<boolean>(false);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [page, setPage] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchPage = useCallback(async (nextPage: number) => {
    setLoading(true);
    try {
      const response = await transactionApi.getUserInvoices({
        page: nextPage,
        limit: HISTORY_PAGE_SIZE,
      });

      if (response?.data?.success) {
        const { data, pagination } = response.data;
        setInvoices((prev) =>
          nextPage === 1 ? data : [...prev, ...data]
        );
        setPage(pagination.page);
        setTotalPages(pagination.totalPages);
      }
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load the first page on mount (dropdown is open by default).
  useEffect(() => {
    fetchPage(1);
  }, [fetchPage]);

  // Toggle open/closed; lazily load the first page if it wasn't loaded yet.
  const handleToggle = () => {
    const nextOpen = !open;
    setOpen(nextOpen);
    if (nextOpen && page === 0) {
      fetchPage(1);
    }
  };

  const hasMore = page > 0 && page < totalPages;

  return (
    <div className="mb-10">
      <button
        type="button"
        onClick={handleToggle}
        aria-expanded={open}
        className="flex items-center justify-between w-full mb-6 text-left"
      >
        <h3 className="text-lg font-semibold text-black">Invoice History</h3>
        <ChevronDownIcon
          className={`w-5 h-5 text-neutral-500 transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open && (
        <div className="overflow-hidden bg-white border rounded-xl border-neutral-200">
          {invoices.length === 0 && !loading ? (
            <div className="p-8 text-center">
              <ReceiptRefundIcon className="w-8 h-8 mx-auto mb-3 text-neutral-300" />
              <p className="text-sm text-neutral-500">No purchases yet</p>
            </div>
          ) : (
            <ul className="divide-y divide-neutral-100">
              {invoices.map((invoice) => (
                <li
                  key={invoice.id}
                  className="flex items-center justify-between gap-4 px-5 py-4"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-neutral-800 truncate">
                      {invoice.packageName || "Pitch Pack"}
                    </p>
                    <p className="mt-1 text-xs text-neutral-500">
                      {safeFormatDate(invoice.invoiceDate, "MMM d, yyyy", "—")}
                      {" · "}
                      {invoice.creditsPurchased} pitches
                      {invoice.invoiceNumber ? ` · ${invoice.invoiceNumber}` : ""}
                    </p>
                  </div>

                  <div className="flex items-center gap-4 shrink-0">
                    <span className="text-sm font-semibold text-neutral-900">
                      {(invoice.currency || "USD").toUpperCase() === "USD"
                        ? `$${Number(invoice.amount).toFixed(2)}`
                        : `${Number(invoice.amount).toFixed(2)} ${(
                            invoice.currency || ""
                          ).toUpperCase()}`}
                    </span>
                    {invoice.pdfPath && (
                      <a
                        href={invoice.pdfPath}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="border border-transparent text-xs font-medium rounded text-primary-700 hover:underline focus:outline-none focus-visible:border-primary-700"
                      >
                        Invoice
                      </a>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {(hasMore || loading) && (
            <div className="px-5 py-4 border-t border-neutral-100">
              <Button
                variant="secondary"
                type="button"
                onClick={() => fetchPage(page + 1)}
                disabled={loading || !hasMore}
                className="w-full"
              >
                {loading ? "Loading..." : "Load more"}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface PitchPackProps {
  onContactSupport?: () => void;
}

const PitchPack: React.FC<PitchPackProps> = ({ onContactSupport }) => {
  const { user, fetchProfile } = useUser();
  const userType = useSelector(selectUserType);
  const [profileData, setProfileData] = useState<ProfileData>({
    purchase: null,
    subscription: null,
  });
  const [products, setProducts] = useState<StripeProduct[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);

  console.log("User Type from Redux:", userType);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch user profile data using the hook
        const userData = await fetchProfile();

        if (userData) {
          // If user type is subscription, fetch subscription details from API
          if (userType === 'subscription') {
            const subscriptionResponse = await userApi.getSubscriptionDetails();
            if (subscriptionResponse?.data?.success) {
              setProfileData({
                purchase: userData.purchase || null,
                subscription: subscriptionResponse.data.data || null,
              });
            } else {
              setProfileData({
                purchase: userData.purchase || null,
                subscription: null,
              });
            }
          } else {
            setProfileData({
              purchase: userData.purchase || null,
              subscription: null,
            });
          }
        }

        // Fetch available Stripe products only if not a subscription user
        if (userType !== 'subscription') {
          const productsResponse = await transactionApi.getAllStripeProducts();
          if (!productsResponse?.data?.success) {
            throw new Error(
              productsResponse?.data?.error || "Failed to fetch products"
            );
          }
          setProducts(productsResponse.data.data || []);
        }
      } catch (error) {
        handleError(error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [fetchProfile, userType]);

  const handlePurchaseMore = () => {
    // Scroll to available plans section
    document
      .getElementById("available-plans")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSelectPlan = (
    paymentLink: string,
    planId: string,
    planName: string
  ) => {
    setSelectedPlan(planId);

    if (paymentLink) {
      // Redirect to the Stripe payment link
      window.location.href = paymentLink.trim();
    } else {
      alert(`No payment link available for ${planName} plan.`);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const response = await userApi.createPortalSession();
      if (response?.data?.success && response.data.data?.url) {
        // Redirect to Stripe Customer Portal
        window.location.href = response.data.data.url;
      } else {
        throw new Error('Failed to create portal session');
      }
    } catch (error) {
      handleError(error);
    }
  };

  // Format price from unit_amount (in cents) and currency
  const formatPrice = (unitAmount: number, currency: string): string => {
    const amountInDollars = (unitAmount / 100).toFixed(2); // Convert cents to dollars
    return currency.toUpperCase() === "USD"
      ? `$${amountInDollars}`
      : `${amountInDollars} ${currency.toUpperCase()}`;
  };

  // Calculate days remaining until expiration
  const getDaysRemaining = (expiryDate: string): number => {
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  if (loading) {
    return <SkeletonLoader />;
  }

  const daysRemaining = profileData.purchase?.expiresAt
    ? getDaysRemaining(profileData.purchase.expiresAt)
    : 0;

  const isPlanExpired = !!profileData.purchase && daysRemaining <= 0;

  // With multiple active packs, each has its own credits and expiry, so the
  // aggregate banner and summary cards no longer describe a single plan — the
  // per-pack table below becomes the source of truth.
  const hasMultiplePacks =
    !!profileData.purchase?.packs && profileData.purchase.packs.length > 1;

  if (userType === 'subscription') {
    const subscription = profileData.subscription;

    return (
      <SettingsPanel title="Subscription Status">

        <div className="p-6 mb-8 border border-primary-200 bg-primary-50 rounded-xl">
          <h3 className="mb-4 text-lg font-medium text-neutral-700">Account Type: Subscription</h3>

          <div className="space-y-3">
            <div className="flex justify-between py-2 border-b border-neutral-200">
              <span className="text-sm font-medium text-neutral-500">Plan:</span>
              <span className="text-sm text-neutral-800">{subscription?.plan_name || 'N/A'}</span>
            </div>

            <div className="flex justify-between py-2 border-b border-neutral-200">
              <span className="text-sm font-medium text-neutral-500">Status:</span>
              <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                subscription?.status === 'active'
                  ? 'bg-success-50 text-success-800 border border-success-200'
                  : subscription?.status === 'past_due'
                  ? 'bg-danger-50 text-danger-700 border border-danger-200'
                  : 'bg-warning-50 text-warning-800'
              }`}>
                {subscription?.status ? subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1) : 'Active'}
              </span>
            </div>

            <div className="flex justify-between py-2 border-b border-neutral-200">
              <span className="text-sm font-medium text-neutral-500">Current Period:</span>
              <span className="text-sm text-neutral-800">
                {safeFormatDate(subscription?.current_period_start, 'MMM d, yyyy')}
                {' - '}
                {safeFormatDate(subscription?.current_period_end, 'MMM d, yyyy')}
              </span>
            </div>

            <div className="flex justify-between py-2 border-b border-neutral-200">
              <span className="text-sm font-medium text-neutral-500">Next Payment:</span>
              <span className="text-sm text-neutral-800">
                {safeFormatDate(subscription?.current_period_end, 'MMM d, yyyy')}
              </span>
            </div>

            <div className="flex justify-between py-2">
              <span className="text-sm font-medium text-neutral-500">Package ID:</span>
              <span className="text-sm text-neutral-800">{subscription?.package_id || 'N/A'}</span>
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <Button
            onClick={handleManageSubscription}
          >
            Cancel Subscription
          </Button>
        </div>
      </SettingsPanel>
    );
  }

  // Header only — rows are composed below so each keeps its expiry annotation.
  const packColumns: DataTableColumn<any>[] = [
    { key: "pack", header: "Pack" },
    { key: "credits", header: "Credits Remaining" },
    { key: "purchased", header: "Purchased" },
    { key: "expires", header: "Expires" },
  ];

  return (
    <SettingsPanel
      title="Your Pitch Pack"
      actions={
        profileData.purchase ? (
          <span
            className={`self-start px-3 py-1 text-xs font-medium rounded-full ${
              isPlanExpired
                ? "text-danger-800 bg-danger-100"
                : "text-success-800 bg-success-100"
            }`}
          >
            {isPlanExpired ? "Expired" : "Active"}
          </span>
        ) : undefined
      }
    >

      {/* Current Plan Overview — hidden for multi-pack users; the per-pack
          table below is the source of truth once several packs are active. */}
      {!hasMultiplePacks && (
        <div className="p-6 mb-8 border border-primary-200 bg-primary-50 rounded-xl">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div className="mb-4 md:mb-0">
              <h3 className="text-lg font-semibold text-black">
                {profileData.purchase?.packageName || "No Active Plan"}
              </h3>
              <p className="mt-1 text-sm text-neutral-500">
                Purchased:{" "}
                {profileData.purchase?.createdAt
                  ? safeFormatDate(profileData.purchase.updatedAt, "MMMM d, yyyy")
                  : "N/A"}
              </p>
            </div>

            {profileData.purchase && (
              <div className="flex items-center">
                <ClockIcon className="w-5 h-5 mr-2 text-primary-700" />
                <span className="text-sm font-medium text-neutral-700">
                  {daysRemaining > 0
                    ? `Expires in ${daysRemaining} days`
                    : "Expired"}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Multi-pack summary — a single aggregate "Remaining Credits" card
          totalling every active pack. We drop the expiry card here because each
          pack expires on its own date; the table below shows those. */}
      {hasMultiplePacks && (
        <div className="p-6 mb-8 bg-white border rounded-xl border-neutral-200">
          <h4 className="mb-1 text-sm font-medium text-neutral-500">
            Remaining Credits
          </h4>
          <div className="flex items-end">
            <span className="text-3xl font-bold text-black">
              {profileData.purchase?.pitchLimit || 0}
            </span>
            <span className="ml-2 text-sm text-neutral-500">pitches</span>
          </div>
          <p className="mt-2 text-xs text-neutral-500">
            across {profileData.purchase?.packs?.length} active packs — see the
            table below for each pack's expiry
          </p>
        </div>
      )}

      {/* Credit Usage and Expiration — the aggregate summary cards only make
          sense for a single active pack. With multiple packs each has its own
          credits and expiry, so we hide these and let the table below be the
          source of truth. */}
      {!hasMultiplePacks && (
        <div className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-2">
          {/* Remaining Credits */}
          <div className="p-6 bg-white border rounded-xl border-neutral-200">
            <h4 className="mb-1 text-sm font-medium text-neutral-500">
              Remaining Credits
            </h4>
            <div className="flex items-end">
              <span className="text-3xl font-bold text-black">
                {profileData.purchase?.pitchLimit || 0}
              </span>
              <span className="ml-2 text-sm text-neutral-500">pitches</span>
            </div>

            {/* Progress bar removed as requested */}
          </div>

          {/* Expiration Date */}
          <div className="p-6 bg-white border rounded-xl border-neutral-200">
            <h4 className="mb-1 text-sm font-medium text-neutral-500">
              Expiration Date
            </h4>
            <div className="flex items-end">
              <span className="text-3xl font-bold text-black">
                {safeFormatDate(profileData.purchase?.expiresAt, "MMM d")}
              </span>
              <span className="ml-2 text-sm text-neutral-500">
                {safeFormatDate(profileData.purchase?.expiresAt, "yyyy", "")}
              </span>
            </div>

            {profileData.purchase?.expiresAt && (
              <div className="flex items-center mt-4">
                <div
                  className={`w-2 h-2 mr-2 rounded-full ${
                    isPlanExpired
                      ? "bg-danger-500"
                      : daysRemaining > 30
                      ? "bg-success-500"
                      : daysRemaining > 7
                      ? "bg-warning-500"
                      : "bg-danger-500"
                  }`}
                ></div>
                <p className="text-xs text-neutral-500">
                  {isPlanExpired
                    ? "Your plan has expired"
                    : daysRemaining > 30
                    ? "Your plan is active for more than a month"
                    : daysRemaining > 7
                    ? `Your plan will expire soon (${daysRemaining} days remaining)`
                    : `Your plan is about to expire (${daysRemaining} days remaining)`}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Active Pitch Packs — shown when more than one pack is active, since a
          single pack is already fully described by the summary cards above.
          Each pack keeps its own credits and expiry (FIFO: oldest spent first). */}
      {hasMultiplePacks && profileData.purchase?.packs && (
        <div className="mb-8">
          <div className="mb-3">
            <h3 className="text-lg font-semibold text-black">
              Your Pitch Packs
            </h3>
            <p className="mt-1 text-xs text-neutral-500">
              Credits are used from the oldest pack first, so expiring credits
              are spent before newer ones.
            </p>
          </div>
          <DataTable columns={packColumns}>
                {profileData.purchase.packs.map((pack) => {
                  const packDaysRemaining = getDaysRemaining(pack.expiresAt);
                  return (
                    <tr key={pack.id} className={dataTableRowClass}>
                      <td className={cn(dataTableTdClass, 'font-medium')}>
                        {pack.packageName || "Pitch Pack"}
                      </td>
                      <td className={cn(dataTableTdClass, 'text-neutral-600')}>
                        {pack.pitchLimit} pitches
                      </td>
                      <td className={cn(dataTableTdClass, 'text-neutral-600')}>
                        {safeFormatDate(pack.purchaseDate, "MMM d, yyyy", "—")}
                      </td>
                      <td className={dataTableTdClass}>
                        <span className="text-neutral-700">
                          {safeFormatDate(pack.expiresAt, "MMM d, yyyy", "—")}
                        </span>
                        {packDaysRemaining > 0 && (
                          <span
                            className={`ml-2 text-xs ${
                              packDaysRemaining > 7
                                ? "text-neutral-400"
                                : "text-danger-500"
                            }`}
                          >
                            ({packDaysRemaining}d left)
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
          </DataTable>
        </div>
      )}

      {/* Purchase More Credits Button */}
      <Button
        onClick={handlePurchaseMore}
        className="w-full mb-8"
      >
        <div className="flex items-center justify-center">
          <CreditCardIcon className="w-5 h-5 mr-2" />
          Purchase More Credits
        </div>
      </Button>

      {/* Purchase History */}
      <PurchaseHistory />

      {/* Available Plans */}
      <div id="available-plans">
        <h3 className="mb-6 text-lg font-semibold text-black">
          Available Plans
        </h3>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {products.length > 0 ? (
            products
              .filter((product) => product.active)
              .filter((product) => {
                const allowedProductIds = [
                  
                  process.env.REACT_APP_PRODUCT_ID_STARTER,       // Starter Pack
                  process.env.REACT_APP_PRODUCT_ID_GROWTH,         // Growth Pack
                  process.env.REACT_APP_PRODUCT_ID_PROFESSIONAL,   // Professional Pack
                ];
                return allowedProductIds.includes(product.id);
              })
              .map((product) => {
                const firstPrice =
                  product.prices && product.prices.length > 0
                    ? product.prices[0]
                    : null;
                const isCurrentPlan =
                  !isPlanExpired &&
                  profileData.purchase?.packageName === product.name;

                return (
                  <div
                    key={product.id}
                    className={`relative overflow-hidden transition-all duration-200 bg-white border rounded-xl ${
                      isCurrentPlan
                        ? "border-primary-700 ring-2 ring-primary-200"
                        : "border-neutral-200 hover:border-primary-300 hover:shadow-md"
                    }`}
                  >
                    {isCurrentPlan && (
                      <div className="absolute top-0 right-0 px-2 py-1 text-xs font-semibold text-white bg-primary-700">
                        Current Plan
                      </div>
                    )}

                    <div className="p-6">
                      <h4 className="mb-2 text-lg font-semibold text-neutral-800">
                        {product.name}
                      </h4>

                      <div className="flex items-baseline mb-4">
                        <span className="text-2xl font-bold text-neutral-900">
                          {firstPrice?.unit_amount && firstPrice?.currency
                            ? formatPrice(
                                firstPrice.unit_amount,
                                firstPrice.currency
                              )
                            : "Price not available"}
                        </span>
                      </div>

                      <p className="mb-6 text-sm text-neutral-600">
                        {product.description}
                      </p>

                      <ul className="mb-6 space-y-2">
                        <li className="flex items-start">
                          <CheckCircleIcon className="w-5 h-5 mr-2 text-success-600 shrink-0" />
                          <span className="text-sm text-neutral-600">
                            {product.metadata.pitch_limit} pitches
                          </span>
                        </li>
                      </ul>

                      <Button
                        variant={isCurrentPlan ? "soft" : "primary"}
                        className="w-full"
                        onClick={() =>
                          handleSelectPlan(
                            product.metadata.payment_link,
                            product.id,
                            product.name
                          )
                        }
                        loading={selectedPlan === product.id}
                      >
                        {isCurrentPlan
                          ? "Buy Again"
                          : selectedPlan === product.id
                          ? "Processing..."
                          : "Buy now"}
                      </Button>
                    </div>
                  </div>
                );
              })
          ) : (
            <div className="p-8 text-center col-span-full bg-neutral-50 rounded-xl">
              <p className="text-neutral-500">No available plans at the moment</p>
            </div>
          )}
        </div>
      </div>

      {/* Help text */}
      <div className="mt-8 text-center">
        <p className="text-sm text-neutral-500">
          Need help with your subscription?{" "}
          <button
            type="button"
            onClick={onContactSupport}
            className="border border-transparent rounded text-primary-700 hover:underline focus:outline-none focus-visible:border-primary-700"
          >
            Contact support
          </button>
        </p>
      </div>
    </SettingsPanel>
  );
};

export default PitchPack;
